import React, { memo } from 'react';
import { constants, messageFormat } from '@clubmed/utils';
import type { ChildcareAgeInMonthsProps } from 'Components/widgets/product/Product.mapper';
import { t } from 'Src/i18n';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { useGQLErrorsHandler } from 'Utils/use-gql-errors-handler/useGQLErrorsHandler';
const { RANKINGS } = constants;
type Props = {
  childcareAgeInMonths: ChildcareAgeInMonthsProps;
  productId: string;
};

const activitiesQuery = (productId: string) => `query {
    activities(productId : "${productId}") {
            id
            isIncluded
            label
            ranking
        }
}`;

const maxActivityToDisplay = 4;
type ActivitiesCardData = {
  activitiesIncluded?: Array<{ id: string; label: string; ranking?: string; isIncluded?: boolean }>;
  excursionsCount?: number;
};

export const ActivitiesProductCard = memo<Props>(({ childcareAgeInMonths, productId }: Props) => {
  const { data, error, isLoading } = useGQL<ActivitiesCardData>({
    query: activitiesQuery(productId),
    queryOptions: {
      cacheFront: {
        key: `PRODUCT_GET_ACTIVITY_${productId}`,
      },
      requestName: 'PRODUCT_GET_ACTIVITY',
    },
    cache: true,
    ignore: !productId,
    map: ({ activities }) => ({
      // excursions
      excursionsCount: activities?.filter(({ id }) => id.includes('EXC')).length,
      // activities Included
      activitiesIncluded: activities?.filter(({ isIncluded }) => isIncluded),
    }),
  });
  useGQLErrorsHandler(error, {
    catchAuthError: true,
  });

  if (!data || error || isLoading) {
    return null;
  }

  const { activitiesIncluded = [], excursionsCount = 0 } = data ?? {};
  // 2 very high activities from included
  const veryHighActivities =
    activitiesIncluded.length > 0
      ? activitiesIncluded.filter(({ ranking }) => ranking === RANKINGS.VERY_HIGH).splice(0, 2)
      : 0;
  // Childcare
  const minAge = childcareAgeInMonths?.reduce(
    (min, { ageInMonthsMin }) => (ageInMonthsMin < min ? ageInMonthsMin : min),
    childcareAgeInMonths[0]?.ageInMonthsMin,
  );
  const maxAge = childcareAgeInMonths?.reduce(
    (max, { ageInMonthsMax }) => (ageInMonthsMax > max ? ageInMonthsMax : max),
    childcareAgeInMonths[0]?.ageInMonthsMax,
  );
  const childcareMessage =
    minAge && maxAge
      ? messageFormat(t('resort.thumbnails.childcare')).format({
          ageMin: Math.floor(minAge / 12),
          ageMax: Math.floor(maxAge / 12),
        })
      : '';
  const excursionsMessage = excursionsCount
    ? messageFormat(t('resort.thumbnails.excursions')).format({
        count: excursionsCount,
      })
    : '';
  const activitiesIncludedMessage = activitiesIncluded.length
    ? messageFormat(t('resort.thumbnails.activities')).format({
        count: activitiesIncluded.length,
      })
    : '';
  const activityList = [
    childcareMessage,
    veryHighActivities,
    activitiesIncludedMessage,
    excursionsMessage,
  ].filter(Boolean);
  const renderActivityList = (
    <ul className="reset-list mt-5 text-12 leading-tight">
      {activityList.map((activity) => {
        if (Array.isArray(activity)) {
          return (
            <li className="w-4/5 truncate">
              {activity.length > 0 &&
                activity.map(({ id, label }) => <span key={id}>{`${label} & `}</span>)}
            </li>
          );
        }

        return (
          <li key={activity} className="truncate">
            {activity}
          </li>
        );
      })}
      {activityList.length > maxActivityToDisplay - 1 && <li>...</li>}
    </ul>
  );
  return renderActivityList;
});
